import type { JournalAccount } from "@/apis/gql/types";
import { gql, useMutation } from "@apollo/client";
import { noop } from "lodash";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

type CreateJournalAccountInput = {
  number?: string | null;
  name?: string | null;
  entityId?: string | null;
  entityType?: string | null;
  accountType?: string | null;
};

export const useCreateJournalAccount = () => {
  const { t } = useTranslation();
  const [createJournalAccount, { data, loading, error }] = useMutation(
    CREATE_JOURNAL_ACCOUNT_MUTATION,
    {
      refetchQueries: ["GetAllJournalAccounts"],
    },
  );

  const handleCreateJournalAccount = async (
    data: CreateJournalAccountInput,
    onSuccess: (data: JournalAccount) => void = noop,
    onError: () => void = noop,
  ) => {
    toast.dismiss();

    const toastId = toast.loading(t("generic_loading"));

    try {
      const response = await createJournalAccount({
        variables: data,
      });

      if (response.errors) {
        throw new Error("error creating journal account");
      }

      onSuccess(response.data.createJournalAccount);

      toast.dismiss(toastId);

      return response;
    } catch (error) {
      onError();
      toast.error(t("generic_error"), {
        id: toastId,
        richColors: true,
      });
      throw error;
    }
  };

  return [handleCreateJournalAccount, { data, loading, error }] as const;
};

export const CREATE_JOURNAL_ACCOUNT_MUTATION = gql`
  mutation (
    $number: String
    $name: String
    $accountType: String
    $entityId: String
    $entityType: String
  ) {
    createJournalAccount(
      number: $number
      name: $name
      accountType: $accountType
      entityId: $entityId
      entityType: $entityType
    ) {
      journalAccountId
      number
      name
      accountType
      entityId
      entityType
    }
  }
`;
